/**
 * Horizon Pro — <pro-slider>: scroll-snap slider used by Pro sections (image/video slider, testimonials...).
 *
 * Attributes:
 *   data-type="slide|loop|fade"      loop wraps around at the ends, fade cross-fades one slide at a time
 *   data-drag="enabled|free|disabled" free has no snapping, disabled only moves with arrows/dots/autoplay
 *   data-autoplay="5" (seconds)       empty = off; pauses on hover and focus
 *   data-center="true"                active slide is centered
 * CSS custom properties on the element (set per breakpoint): --pro-per-page, --pro-per-move,
 *   --pro-adaptive (1 = track height follows the active slide when one slide per page).
 * Children: [data-pro-slider-track] with slides, optional [data-pro-slider-prev]/[data-pro-slider-next],
 * [data-pro-slider-dots] (dots are generated; data-style="numbers" shows page numbers),
 * [data-pro-slider-counter] ("2 / 5") and [data-pro-slider-toggle] (pause/play autoplay).
 */
class ProSlider extends HTMLElement {
  #index = 0;
  #timer = 0;
  #paused = false;
  #frame = 0;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #drag = { active: false, startX: 0, startScroll: 0, moved: false };

  connectedCallback() {
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-slider-track]'));
    if (!this.track) return;

    this.addEventListener('click', this.#onClick);
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('mouseenter', this.#stopAutoplay);
    this.addEventListener('mouseleave', this.#startAutoplay);
    this.addEventListener('focusin', this.#stopAutoplay);
    this.addEventListener('focusout', this.#startAutoplay);

    this.#resizeObserver = new ResizeObserver(() => this.#refresh());
    this.#resizeObserver.observe(this.track);

    this.#refresh();
    this.#startAutoplay();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onEditorSelect);
    }
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#resizeObserver?.disconnect();
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
  }

  get slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.track?.children ?? []).filter((el) => !el.hasAttribute('hidden')));
  }

  get isFade() {
    return this.dataset.type === 'fade';
  }

  get isLoop() {
    return this.dataset.type === 'loop';
  }

  #cssNumber(name, fallback) {
    const value = parseFloat(getComputedStyle(this).getPropertyValue(name));
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  get perPage() {
    return this.isFade ? 1 : Math.round(this.#cssNumber('--pro-per-page', 1));
  }

  get perMove() {
    return Math.max(1, Math.round(this.#cssNumber('--pro-per-move', 1)));
  }

  /** Last index the track can scroll to (the end of the last page) */
  get maxIndex() {
    if (this.isFade || this.dataset.center === 'true') return Math.max(0, this.slides.length - 1);
    return Math.max(0, this.slides.length - this.perPage);
  }

  /** Slide indexes that start a page (one dot per page) */
  get pageStarts() {
    const starts = [];
    for (let i = 0; i < this.maxIndex; i += this.perMove) starts.push(i);
    starts.push(this.maxIndex);
    return [...new Set(starts)];
  }

  /**
   * @param {number} index
   * @param {boolean} [smooth]
   */
  goTo(index, smooth = true) {
    const slides = this.slides;
    if (!this.track || slides.length === 0) return;

    let target = index;
    if (this.isLoop) {
      if (target > this.maxIndex) target = 0;
      else if (target < 0) target = this.maxIndex;
    }
    target = Math.max(0, Math.min(target, this.maxIndex));

    if (!this.isFade) {
      const slide = slides[target];
      if (slide) {
        const padding = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
        let left = slide.offsetLeft - padding;
        if (this.dataset.center === 'true') {
          left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
        }
        this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
      }
    }
    this.#setActive(target);
  }

  next() {
    this.goTo(this.#index + this.perMove);
  }

  prev() {
    this.goTo(this.#index - this.perMove);
  }

  #refresh() {
    this.#renderDots();
    this.goTo(Math.min(this.#index, this.maxIndex), false);
    this.classList.toggle('pro-slider--static', this.maxIndex === 0);
  }

  #renderDots() {
    const starts = this.pageStarts;
    for (const container of this.querySelectorAll('[data-pro-slider-dots]')) {
      if (container.childElementCount === starts.length) continue;
      container.replaceChildren(
        ...starts.map((start, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'pro-slider__dot';
          dot.dataset.proSliderGo = String(start);
          if (container instanceof HTMLElement && container.dataset.style === 'numbers') dot.textContent = String(i + 1);
          dot.setAttribute('aria-label', `${i + 1} / ${starts.length}`);
          return dot;
        })
      );
    }
  }

  /** @param {number} index */
  #setActive(index) {
    this.#index = index;
    const slides = this.slides;
    slides.forEach((slide, i) => {
      const visible = i >= index && i < index + this.perPage;
      slide.classList.toggle('is-active', i === index);
      slide.classList.toggle('is-visible', visible);
      if (this.isFade) slide.setAttribute('aria-hidden', String(i !== index));
    });

    const starts = this.pageStarts;
    let page = 0;
    starts.forEach((start, i) => {
      if (index >= start) page = i;
    });
    for (const container of this.querySelectorAll('[data-pro-slider-dots]')) {
      Array.from(container.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === page);
        if (i === page) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    for (const counter of this.querySelectorAll('[data-pro-slider-counter]')) {
      counter.textContent = `${page + 1} / ${starts.length}`;
    }

    if (!this.isLoop) {
      this.querySelectorAll('[data-pro-slider-prev]').forEach((b) => /** @type {HTMLButtonElement} */ (b).toggleAttribute('disabled', index <= 0));
      this.querySelectorAll('[data-pro-slider-next]').forEach((b) => /** @type {HTMLButtonElement} */ (b).toggleAttribute('disabled', index >= this.maxIndex));
    }

    if (this.#cssNumber('--pro-adaptive', 0) === 1 && this.perPage === 1 && this.track) {
      const active = slides[index];
      if (active) this.track.style.height = `${active.offsetHeight}px`;
    } else if (this.track) {
      this.track.style.removeProperty('height');
    }

    this.dispatchEvent(new CustomEvent('pro-slider:change', { detail: { index }, bubbles: true }));
  }

  #onScroll = () => {
    if (this.isFade || this.#drag.active) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const track = this.track;
      if (!track) return;
      const center = this.dataset.center === 'true';
      const padding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
      const reference = center ? track.scrollLeft + track.clientWidth / 2 : track.scrollLeft + padding;
      let closest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, i) => {
        const position = center ? slide.offsetLeft + slide.offsetWidth / 2 : slide.offsetLeft;
        const d = Math.abs(position - reference);
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      // At the very end the last page may not reach its snap point: treat it as the last index.
      if (!center && track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) closest = this.maxIndex;
      if (closest !== this.#index) this.#setActive(Math.min(closest, this.maxIndex));
    });
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (this.#drag.moved) {
      event.preventDefault();
      event.stopPropagation();
      this.#drag.moved = false;
      return;
    }
    const toggle = target.closest('[data-pro-slider-toggle]');
    if (toggle instanceof HTMLElement) return this.#toggleAutoplay(toggle);
    if (target.closest('[data-pro-slider-prev]')) return this.prev();
    if (target.closest('[data-pro-slider-next]')) return this.next();
    const go = target.closest('[data-pro-slider-go]');
    if (go) this.goTo(Number(go.getAttribute('data-pro-slider-go')));
  };

  /** Mouse drag on desktop (touch devices scroll natively) */
  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.isFade || this.dataset.drag === 'disabled' || !this.track) return;
    const track = this.track;
    this.#drag = { active: true, startX: event.clientX, startScroll: track.scrollLeft, moved: false };
    track.classList.add('is-dragging');

    const move = (/** @type {PointerEvent} */ e) => {
      const delta = e.clientX - this.#drag.startX;
      if (Math.abs(delta) > 5) this.#drag.moved = true;
      track.scrollLeft = this.#drag.startScroll - delta;
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      track.classList.remove('is-dragging');
      this.#drag.active = false;
      if (this.dataset.drag !== 'free') {
        this.#onScroll();
        requestAnimationFrame(() => this.goTo(this.#index));
      }
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  /** @param {HTMLElement} button */
  #toggleAutoplay(button) {
    this.#paused = !this.#paused;
    this.classList.toggle('pro-slider--paused', this.#paused);
    const label = this.#paused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
    if (this.#paused) {
      this.#stopAutoplay();
    } else {
      this.#startAutoplay();
    }
  }

  #startAutoplay = () => {
    const seconds = parseFloat(this.dataset.autoplay || '');
    if (!seconds || this.#timer || this.#paused) return;
    this.#timer = window.setInterval(() => {
      if (document.hidden) return;
      if (!this.isLoop && this.#index >= this.maxIndex) this.goTo(0);
      else this.next();
    }, seconds * 1000);
  };

  #stopAutoplay = () => {
    window.clearInterval(this.#timer);
    this.#timer = 0;
  };

  /** @param {any} event */
  #onEditorSelect = (event) => {
    if (!this.contains(event.target)) return;
    const slide = event.target.closest?.('[data-pro-slider-track] > *');
    const index = this.slides.indexOf(slide);
    if (index >= 0) {
      this.#stopAutoplay();
      this.goTo(index);
    }
  };
}

if (!customElements.get('pro-slider')) customElements.define('pro-slider', ProSlider);
